// Cap canvas dimensions so watermarking works within mobile canvas/memory
// limits. iOS Safari caps canvas area at ~16.7MP; large Android canvases fail
// to allocate. 4096px on the longest edge keeps quality high while staying
// safely under those limits (a 32MP original fails outright on mobile).
const MAX_DIMENSION = 4096

export const generateWatermarkedImage = async (
  imageUrl: string,
  watermarkText: string
): Promise<Blob> => {
  // Fetch the image as a blob first. This relies on the CORS-enabled endpoint
  // and avoids the crossOrigin <img> cache-poisoning issue. Decoding via
  // createImageBitmap is far more memory-efficient on mobile than an <img>
  // element, which fails to decode very large photos on Android/iOS and fires
  // onerror ("Failed to load image").
  // cache: 'no-store' forces a fresh cross-origin request that carries the
  // Origin header, so R2 returns the CORS header. Without it the browser can
  // reuse the gallery <img>'s cached response (fetched with no Origin, and no
  // Vary: Origin), which lacks Access-Control-Allow-Origin -> "Failed to fetch".
  const response = await fetch(imageUrl, { mode: 'cors', cache: 'no-store' })
  if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`)
  const blob = await response.blob()

  let source: ImageBitmap
  try {
    source = await createImageBitmap(blob)
  } catch {
    throw new Error('Failed to decode image')
  }

  // Downscale large images to a mobile-safe size
  const scale = Math.min(1, MAX_DIMENSION / Math.max(source.width, source.height))
  const width = Math.round(source.width * scale)
  const height = Math.round(source.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    source.close()
    throw new Error('Could not get canvas context')
  }

  // Draw the (possibly downscaled) image
  ctx.drawImage(source, 0, 0, width, height)
  source.close()

  // Configure watermark style relative to the final canvas dimensions
  const fontSize = Math.max(width, height) * 0.02 // 2% of the larger dimension
  const padding = fontSize * 0.5
  const borderRadius = fontSize * 0.3

  // Draw watermark with background in the bottom-right corner
  ctx.font = `${fontSize}px Arial, sans-serif`
  ctx.textBaseline = 'top'

  const textMetrics = ctx.measureText(watermarkText)
  const bgWidth = textMetrics.width + (padding * 2)
  const bgHeight = fontSize + (padding * 2)

  const margin = Math.max(width, height) * 0.02 // 2% margin from edges
  const bgX = width - margin - bgWidth
  const bgY = height - margin - bgHeight

  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  ctx.beginPath()
  ctx.roundRect(bgX, bgY, bgWidth, bgHeight, borderRadius)
  ctx.fill()

  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
  ctx.textAlign = 'left'
  ctx.fillText(watermarkText, bgX + padding, bgY + padding)

  // Convert canvas to blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result)
      } else {
        reject(new Error('Could not generate watermarked image'))
      }
    }, 'image/jpeg', 0.9)
  })
}

export const isImage = (url: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
  const urlLower = url.toLowerCase()
  return imageExtensions.some(ext => urlLower.includes(ext))
}